import { collection, query, where, getDocs, doc, updateDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { getAuth } from 'firebase/auth';

interface UpdatePriceData {
  minimumPrice: number;
  proposedPrice: number;
  commission: number;
  valueToReceive: number;
  priceHistory: {
    date: Timestamp;
    oldPrice: number;
    newPrice: number;
    updatedBy: string;
    updatedByName: string;
  }[];
}

export async function getMyProducts() {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error('Utilizador não autenticado');
    }

    const q = query(
      collection(db, 'marketplaceProductSubmissions'),
      where('submittedBy', '==', currentUser.uid)
    );

    const querySnapshot = await getDocs(q);
    const productsData = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      productsData.push({
        id: doc.id,
        ean: data.ean,
        productName: data.productName,
        proposedPrice: data.proposedPrice,
        stockQuantity: data.stockQuantity,
        desiredChannel: data.desiredChannel || 'amazon',
        commission: data.commission,
        valueToReceive: data.valueToReceive,
        ptPrice: data.ptPrice,
        buyboxPrice: data.buyboxPrice,
        isActive: data.isActive,
        status: data.status || 'pending',  
        asin: data.asin,
        mainImage: data.mainImage,
        minimumPrice: data.minimumPrice,
        commissionVAT: data.commissionVAT,
        esPrice: data.esPrice,
        lastPriceSync: data.lastPriceSync,
        lastSyncedPrice: data.lastSyncedPrice,
        publishedAt: data.publishedAt,
        publishedDescription: data.publishedDescription,
        publishedTitle: data.publishedTitle,
        publishedImageUrl: data.publishedImageUrl,
        recommendedPrice: data.recommendedPrice,
        reviewedAt: data.reviewedAt,
        approvedAt: data.approvedAt,
        submittedAt: data.submittedAt,
        submittedBy: data.submittedBy,
        submitterName: data.submitterName,
        adminNotes: data.adminNotes,
        sku: data.sku,
        totalOffers: data.totalOffers,
        priceHistory: data.priceHistory || []
      });
    });

    return productsData;
  } catch (error) {
    console.error('Erro ao carregar produtos:', error);
    throw error;
  }
}

export async function updateProductPrice(productId: string, data: Omit<UpdatePriceData, 'priceHistory'>) {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;

    if (!currentUser) {
      throw new Error('Utilizador não autenticado');
    }

    // Obter o documento atual para pegar o histórico de preços
    const productRef = doc(db, 'marketplaceProductSubmissions', productId);
    const productDoc = await getDoc(productRef);
    
    if (!productDoc.exists()) {
      throw new Error('Produto não encontrado');
    }

    const currentData = productDoc.data();
    const currentHistory = currentData.priceHistory || [];

    // Criar nova entrada no histórico
    const newHistoryEntry = {
      date: Timestamp.now(),
      oldPrice: currentData.proposedPrice,
      newPrice: data.proposedPrice,
      updatedBy: currentUser.uid,
      updatedByName: currentUser.email || 'Vendedor'
    };

    // Atualizar o documento com os novos dados e o histórico atualizado
    await updateDoc(productRef, {
      ...data,
      status: 'pending', // Volta para pendente quando o preço é alterado
      priceHistory: [...currentHistory, newHistoryEntry]
    });
  } catch (error) {
    console.error('Erro ao atualizar preços:', error);
    throw error;
  }
}
